import { createApp, watch } from 'vue'
import '@sennder/design-system-core'
import { loadStyles, themeProvider, Themes } from '@sennder/design-system-core'
import { envOverrides } from '@sennder/shell-utilities'
import { Shell } from '@sennder/microfrontend-registry'
import { SharedDataType } from '@sennder/senn-node-microfrontend-interfaces'

import '@/assets/tailwind.global.css'
import '@sennder/shell-utilities/dist/style.css'

import App from '@/App.vue'
import router from '@/router'
import { ENV, LD_KEY, isLocalEnv } from '@/common/config'
import { registerPlugins } from '@/plugins'
import { translationProvider } from '@/services/translationProvider'
import { loadUserData } from '@/store'
import { logger } from '@/services/logger/loggers'
import { registerModalManager } from '@/components/modal-manager'
import { getCookieConsent } from '@/services/cookies'
import { startTracking } from '@/services/tracking'
import { featureFlag } from '@/services/featureFlagProvider'
import { carrierProfilePublicService } from '@/services/carrierProfilePublicService'
import { initRouter } from './router/routes'
import { microfrontends } from './config/microfrontends'
import { getMfContext, shell, shellLogLevel } from './config/registry'
import { getStateData } from './store/getters'
import { initSessionReplay, stopSessionReplay } from '@/services/analytics'
import { setStoreLegacyFlags } from '@/store/setters'

async function initialize() {
  window.onerror = function (message, url, line, column, error) {
    logger.error('[Orcas shell - global error]', { error })
  }

  carrierProfilePublicService.cleanupLocalstorage()

  shell.value = new Shell(
    SharedDataType.ORCAS,
    getMfContext,
    ENV,
    'none',
    shellLogLevel
  )

  if (!LD_KEY) {
    throw new Error('LD_KEY not found')
  }

  if (isLocalEnv()) {
    envOverrides.load({
      logger: logger,
      legacyModules: Object.values(microfrontends),
    })
  }

  registerModalManager()

  await Promise.all([
    featureFlag.init(LD_KEY),
    translationProvider.initialize(),
    loadUserData(),
  ])
}

async function checkCookieConsent() {
  try {
    const consent = await getCookieConsent()
    if (consent.ANALYTICS) {
      startTracking()
    }
    return consent
  } catch (error) {
    logger.error(
      `[orcas-shell - checkCookieConsent] Error checking cookie consent`,
      { error }
    )
  }
}

async function start() {
  await loadStyles('body')
  themeProvider('html', Themes.Light)
  // don't load data and mount the app until user gives cookie consent
  await checkCookieConsent()

  try {
    await initialize()
  } catch (error) {
    logger.error(`Orcas shell init`, { error })
  } finally {
    const app = createApp(App)
    const { unmount } = app

    const stopWatchData = watch(getStateData, () => {
      shell.value?.onSharedDataChange()
    })

    // This should be the only watchAllFlags call in the application to avoid noisy logging to LaunchDarkly. Otherwise, each watchAllFlags will report another set of feature flag evaluations.
    const stopWatchLegacyFlags =
      featureFlag.devFunctions.watchAllFlags(setStoreLegacyFlags)

    const stopWatchAmplitudeReplayFlag = featureFlag.watchFeatureFlag(
      'ENABLE_AMPLITUDE_SESSION_REPLAY',
      (enable: boolean) => {
        if (enable) {
          initSessionReplay()
        } else {
          stopSessionReplay()
        }
      }
    )

    app.unmount = (...args) => {
      unmount(...args)
      stopWatchData()
      stopWatchLegacyFlags()
      stopWatchAmplitudeReplayFlag()
      shell.value?.destroy()
    }

    await initRouter()
    app.use(router)

    registerPlugins(app)

    app.mount('#app')
  }
}

start()
